import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Text,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Tooltip,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  VStack,
  HStack,
  SimpleGrid,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { Plus, PencilSimple, Trash, Eye, Star } from 'phosphor-react';
import { useActionToast } from '../../services/actionToast';
import { PageTitle } from '../../components/PageTitle';
import { DataSkeleton, EmptyState, ErrorState } from '../../components/states';
import { useFetch } from '../../hooks/useFetch';
import { api } from '../../services/api';

interface MaterialModule {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  is_default: boolean;
  display_order: number;
  templates_count: number;
  institutions_count: number;
  created_at: string;
}

interface Template {
  id: number;
  name: string;
  type: string;
  category: string;
  school_class: string;
}

export function MaterialModulesPage() {
  const [selectedModule, setSelectedModule] = useState<MaterialModule | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    is_default: false,
    display_order: 0,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const toast = useActionToast();

  const {
    requestApi: fetchModules,
    data: modulesData,
    loading: loadingModules,
    error: modulesError,
  } = useFetch('/v1/admin/material-modules', { actionFeedback: false });

  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    await fetchModules('get');
  };

  const handleViewModule = async (module: MaterialModule) => {
    setSelectedModule(module);
    setLoadingTemplates(true);
    setTemplatesError(false);
    setTemplates([]);
    onViewOpen();

    try {
      const response = await api.get(`/v1/admin/material-modules/${module.id}?with_templates=true`);
      setTemplates(response.data.templates || []);
    } catch {
      setTemplatesError(true);
      toast({
        title: 'Erro ao carregar templates',
        description: 'Não foi possível carregar os templates do módulo',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleCreate = () => {
    setFormData({
      name: '',
      description: '',
      is_active: true,
      is_default: false,
      display_order: 0,
    });
    setIsEditing(false);
    onOpen();
  };

  const handleEdit = (module: MaterialModule) => {
    setFormData({
      name: module.name,
      description: module.description,
      is_active: module.is_active,
      is_default: module.is_default,
      display_order: module.display_order,
    });
    setSelectedModule(module);
    setIsEditing(true);
    onOpen();
  };

  const handleSave = async () => {
    try {
      if (isEditing && selectedModule) {
        await api.put(`/v1/admin/material-modules/${selectedModule.id}`, formData);
        toast({
          title: 'Módulo atualizado',
          status: 'success',
          duration: 2000,
        });
      } else {
        await api.post('/v1/admin/material-modules', formData);
        toast({
          title: 'Módulo criado',
          status: 'success',
          duration: 2000,
        });
      }

      onClose();
      loadModules();
    } catch {
      toast({
        title: 'Erro ao salvar módulo',
        description: 'Não foi possível salvar o módulo.',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleDelete = async (module: MaterialModule) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Tem certeza que deseja deletar o módulo "${module.name}"?`)) {
      return;
    }

    try {
      await api.delete(`/v1/admin/material-modules/${module.id}`);
      toast({
        title: 'Módulo deletado',
        status: 'success',
        duration: 2000,
      });
      loadModules();
    } catch {
      toast({
        title: 'Erro ao deletar módulo',
        description: 'Não foi possível excluir o módulo.',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const modules: MaterialModule[] = Array.isArray(modulesData) ? modulesData : [];

  const renderModulesContent = () => {
    if ((loadingModules || !modulesData) && !modulesError && modules.length === 0) {
      return <DataSkeleton variant="table" rows={5} columns={7} label="Carregando módulos" />;
    }

    if (modulesError && modules.length === 0) {
      return (
        <ErrorState
          description="Não foi possível carregar os módulos de materiais."
          onRetry={loadModules}
        />
      );
    }

    if (modules.length === 0) {
      return (
        <EmptyState
          title="Nenhum módulo configurado"
          description="Crie o primeiro módulo para organizar os materiais."
          action={{ label: 'Criar primeiro módulo', onClick: handleCreate }}
        />
      );
    }

    return (
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>Ordem</Th>
            <Th>Nome</Th>
            <Th>Descrição</Th>
            <Th>Templates</Th>
            <Th>Instituições</Th>
            <Th>Status</Th>
            <Th width="150px">Ações</Th>
          </Tr>
        </Thead>
        <Tbody>
          {modules.map((module) => (
            <Tr key={module.id} data-testid={`module-row-${module.id}`}>
              <Td>{module.display_order}</Td>
              <Td>
                <Flex align="center" gap={2}>
                  <Text fontWeight="500">{module.name}</Text>
                  {module.is_default && (
                    <Tooltip label="Módulo padrão">
                      <Box>
                        <Star size={16} weight="fill" color="#F59E0B" />
                      </Box>
                    </Tooltip>
                  )}
                </Flex>
              </Td>
              <Td>
                <Text fontSize="sm" color="gray.600" noOfLines={2}>
                  {module.description}
                </Text>
              </Td>
              <Td>
                <Badge colorScheme="blue">{module.templates_count}</Badge>
              </Td>
              <Td>
                <Badge colorScheme="green">{module.institutions_count}</Badge>
              </Td>
              <Td>
                <Badge colorScheme={module.is_active ? 'green' : 'gray'}>
                  {module.is_active ? 'Ativo' : 'Inativo'}
                </Badge>
              </Td>
              <Td>
                <HStack spacing={1}>
                  <Tooltip label="Ver detalhes">
                    <IconButton
                      aria-label="Ver detalhes"
                      icon={<Eye size={18} />}
                      size="sm"
                      variant="ghost"
                      onClick={() => handleViewModule(module)}
                      data-testid={`view-module-${module.id}`}
                    />
                  </Tooltip>
                  <Tooltip label="Editar">
                    <IconButton
                      aria-label="Editar"
                      icon={<PencilSimple size={18} />}
                      size="sm"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={() => handleEdit(module)}
                      data-testid={`edit-module-${module.id}`}
                    />
                  </Tooltip>
                  <Tooltip label="Deletar">
                    <IconButton
                      aria-label="Deletar"
                      icon={<Trash size={18} />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => handleDelete(module)}
                      isDisabled={module.institutions_count > 0}
                      data-testid={`delete-module-${module.id}`}
                    />
                  </Tooltip>
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  };

  return (
    <Box>
      <PageTitle>Módulos de Materiais Extras</PageTitle>

      <Box bg="white" p={6} borderRadius="md" shadow="sm" mb={4}>
        <Alert status="info" mb={4}>
          <AlertIcon />
          <Box>
            <Text fontSize="sm" fontWeight="600" mb={1}>
              O que são Módulos de Materiais?
            </Text>
            <Text fontSize="sm">
              Módulos são abstrações pedagógicas que agrupam materiais extras relacionados. Eles
              funcionam como &quot;pacotes&quot; que podem ser reutilizados em diferentes templates
              e instituições, facilitando a organização e padronização dos conteúdos. Por exemplo:
              um módulo &quot;Fundamental I&quot; pode conter todas as categorias e materiais
              específicos para essa etapa de ensino, e ser aplicado a múltiplas instituições de uma
              só vez.
            </Text>
          </Box>
        </Alert>

        {!modulesError && (
          <Flex justify="space-between" align="center" mb={4}>
            <Text fontSize="lg" fontWeight="600">
              {modules.length} módulo(s) configurado(s)
            </Text>
            <Button
              leftIcon={<Plus size={20} />}
              colorScheme="blue"
              onClick={handleCreate}
              data-testid="create-module-button"
            >
              Novo Módulo
            </Button>
          </Flex>
        )}

        {renderModulesContent()}
      </Box>

      {/* Modal de Criar/Editar */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{isEditing ? 'Editar Módulo' : 'Criar Novo Módulo'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Nome do Módulo</FormLabel>
                <Input
                  placeholder="Ex: Fundamental I - Completo"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="module-name-input"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Descrição</FormLabel>
                <Textarea
                  placeholder="Descrição do módulo e quando usá-lo..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  data-testid="module-description-input"
                />
              </FormControl>

              <SimpleGrid columns={2} spacing={4} width="100%">
                <FormControl>
                  <FormLabel>Ordem de Exibição</FormLabel>
                  <Input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        display_order: Number.isNaN(parseInt(e.target.value, 10))
                          ? 0
                          : parseInt(e.target.value, 10),
                      })
                    }
                    data-testid="module-order-input"
                  />
                </FormControl>

                <FormControl display="flex" alignItems="center" pt={8}>
                  <FormLabel mb={0}>Módulo Ativo</FormLabel>
                  <Switch
                    isChecked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    data-testid="module-active-switch"
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0}>
                  Módulo Padrão
                  <Text fontSize="xs" color="gray.500" fontWeight="normal">
                    Será aplicado automaticamente ao criar novas instituições
                  </Text>
                </FormLabel>
                <Switch
                  isChecked={formData.is_default}
                  onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                  data-testid="module-default-switch"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button colorScheme="blue" onClick={handleSave} data-testid="save-module-button">
              {isEditing ? 'Atualizar' : 'Criar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal de Visualização */}
      <Modal isOpen={isViewOpen} onClose={onViewClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Detalhes do Módulo: {selectedModule?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {loadingTemplates && (
              <DataSkeleton variant="detail" rows={5} label="Carregando detalhes do módulo" />
            )}
            {!loadingTemplates && templatesError && (
              <ErrorState
                onRetry={() => selectedModule && handleViewModule(selectedModule)}
                description="Não foi possível carregar os templates do módulo."
              />
            )}
            {!loadingTemplates && !templatesError && (
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontWeight="600" mb={2}>
                    Descrição:
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {selectedModule?.description}
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="600" mb={2}>
                    Templates ({templates.length}):
                  </Text>
                  {templates.length === 0 ? (
                    <EmptyState
                      title="Nenhum template vinculado"
                      description="Este módulo ainda não contém templates."
                    />
                  ) : (
                    <Box maxH="400px" overflowY="auto">
                      <Table size="sm" variant="simple">
                        <Thead position="sticky" top={0} bg="white">
                          <Tr>
                            <Th>Nome</Th>
                            <Th>Tipo</Th>
                            <Th>Categoria</Th>
                            <Th>Turma</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {templates.map((template) => (
                            <Tr key={template.id}>
                              <Td fontSize="sm">{template.name}</Td>
                              <Td>
                                <Badge size="sm" colorScheme="purple">
                                  {template.type}
                                </Badge>
                              </Td>
                              <Td fontSize="sm">{template.category}</Td>
                              <Td fontSize="sm">{template.school_class}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  )}
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onViewClose}>Fechar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default MaterialModulesPage;
